/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Display accessors for property list/detail: nested ADL-010 first, flat fallback.
 *
 * Testing still has flat seed rows while the UI reads nested VOs. These helpers
 * keep both shapes rendering correctly during the migration.
 */

type AnyRecord = Record<string, unknown>;
type PriceBlock = { price?: number };

/**
 * Returns the first defined candidate (including empty string / 0 / false).
 *
 * @param values - Candidates in priority order.
 * @returns The first defined value, or undefined.
 */
function firstDefined<T>(...values: Array<T | null | undefined>): T | undefined {
  for (const value of values) {
    if (value !== undefined && value !== null) return value;
  }
  return undefined;
}

/**
 * Reads a nested object section when present.
 *
 * @param raw - Property payload.
 * @param key - Section key.
 * @returns The section object, or an empty object.
 */
function section(raw: AnyRecord, key: string): AnyRecord {
  const value = raw[key];
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as AnyRecord)
    : {};
}

/**
 * Resolves the canonical property name from description.headline only.
 *
 * @param property - Property payload.
 * @returns Headline string when present.
 */
export function propertyHeadline(property: AnyRecord): string | undefined {
  const desc = property.description;
  if (desc && typeof desc === 'object') {
    const headline = (desc as AnyRecord).headline;
    if (typeof headline === 'string') return headline;
  }
  return undefined;
}

/**
 * Resolves city/state for list subtitles.
 *
 * @param property - Property payload.
 * @returns `{ city, state }` with nested-first fallback.
 */
export function propertyCityState(property: AnyRecord): { city?: string; state?: string } {
  const loc = section(property, 'location');
  return {
    city: firstDefined(loc.city as string | undefined, property.city as string | undefined),
    state: firstDefined(loc.state as string | undefined, property.state as string | undefined),
  };
}

/**
 * Resolves category label.
 *
 * @param property - Property payload.
 * @returns Category when present.
 */
export function propertyCategory(property: AnyRecord): string | undefined {
  const cls = section(property, 'classification');
  return firstDefined(
    cls.category as string | undefined,
    property.category as string | undefined,
  );
}

/**
 * Resolves total area.
 *
 * @param property - Property payload.
 * @returns Total area when present.
 */
export function propertyTotalArea(property: AnyRecord): number | undefined {
  const chars = section(property, 'characteristics');
  return firstDefined(
    chars.total_area as number | undefined,
    property.total_area as number | undefined,
  );
}

/**
 * Resolves sale/rental price blocks.
 *
 * @param property - Property payload.
 * @returns Pricing blocks with nested-first fallback.
 */
export function propertyPricing(property: AnyRecord): {
  sale?: PriceBlock;
  rental?: PriceBlock;
} {
  const pricing = section(property, 'pricing');
  return {
    sale: firstDefined(pricing.sale as PriceBlock | undefined, property.sale as PriceBlock | undefined),
    rental: firstDefined(
      pricing.rental as PriceBlock | undefined,
      property.rental as PriceBlock | undefined,
    ),
  };
}

/**
 * Maps a nested classification status string to a display status.
 *
 * @param status - Raw nested status value.
 * @returns Display status, or undefined when not a string.
 */
function statusFromClassification(status: unknown): 'active' | 'inactive' | undefined {
  if (typeof status !== 'string') return undefined;
  return status === 'inactive' ? 'inactive' : 'active';
}

/**
 * Maps a flat `active` boolean to a display status.
 *
 * @param active - Flat active flag.
 * @returns Display status, or undefined when not a boolean.
 */
function statusFromActiveFlag(active: unknown): 'active' | 'inactive' | undefined {
  if (typeof active !== 'boolean') return undefined;
  return active ? 'active' : 'inactive';
}

/**
 * Resolves active/inactive display status.
 *
 * @param property - Property payload.
 * @returns `active` or `inactive`.
 */
export function propertyStatus(property: AnyRecord): 'active' | 'inactive' {
  const cls = section(property, 'classification');
  return (
    statusFromClassification(cls.status) ??
    statusFromActiveFlag(property.active) ??
    'active'
  );
}

/**
 * Resolves transaction type labels (venda/aluguel).
 *
 * @param property - Property payload.
 * @returns Transaction type list (may be empty).
 */
export function propertyTransactionTypes(property: AnyRecord): string[] {
  const cls = section(property, 'classification');
  if (Array.isArray(cls.transaction_types)) return cls.transaction_types as string[];
  if (Array.isArray(property.transaction_type)) return property.transaction_type as string[];
  return [];
}

/** The business badge of a listing (see {@link propertyBusinessType}). */
export type PropertyBusinessType = 'sale' | 'rental' | 'both' | 'short_term' | 'auction' | 'none';

/**
 * Maps transaction types onto sale / rental / both, then Temporada / Leilão.
 *
 * Venda and Aluguel win when present. A listing offered only as Temporada
 * is `short_term`, never `rental`: short-term letting is not Aluguel. One
 * offered only at auction is `auction`. Transaction types are optional: a
 * listing with none is `none` (no badge), never a guessed `sale`.
 *
 * @param property - Property payload.
 * @returns Business badge type; `sale` for an unrecognised value (Permuta).
 */
export function propertyBusinessType(property: AnyRecord): PropertyBusinessType {
  const types = propertyTransactionTypes(property).map((t) => t.toLowerCase());
  if (types.length === 0) return 'none';
  const hasSale = types.some((t) => t.includes('venda'));
  const hasRental = types.some((t) => t.includes('aluguel'));
  return saleRentalBusinessType(hasSale, hasRental) ?? secondaryBusinessType(types);
}

/**
 * The badge when Venda or Aluguel is offered; they win over the rest.
 *
 * @param hasSale - Whether Venda is offered.
 * @param hasRental - Whether Aluguel is offered.
 * @returns 'both', 'rental' or 'sale', or undefined when neither is offered.
 */
function saleRentalBusinessType(hasSale: boolean, hasRental: boolean): PropertyBusinessType | undefined {
  if (hasSale && hasRental) return 'both';
  if (hasRental) return 'rental';
  return hasSale ? 'sale' : undefined;
}

/**
 * The badge of a listing without Venda or Aluguel.
 *
 * @param types - Lower-cased transaction types.
 * @returns 'short_term', 'auction', or 'sale' for an unrecognised value (Permuta).
 */
function secondaryBusinessType(types: string[]): PropertyBusinessType {
  if (types.includes('temporada')) return 'short_term';
  return types.includes('leilão') ? 'auction' : 'sale';
}

/**
 * Composes a single-line human-readable address from nested location fields.
 * Format: "Street, Number · Neighborhood · City/State"
 *
 * @param property - Property payload (nested or flat).
 * @returns Address string, or undefined when no location fields are present.
 */
export function propertyAddressLine(property: AnyRecord): string | undefined {
  const loc = section(property, 'location');
  const street = firstDefined(loc.street as string | undefined, property.street as string | undefined);
  const number = firstDefined(loc.number as string | undefined, property.number as string | undefined);
  const neighborhood = firstDefined(loc.neighborhood as string | undefined, property.neighborhood as string | undefined);
  const { city, state } = propertyCityState(property);
  const streetPart = [street, number].filter(Boolean).join(', ');
  const cityPart = [city, state].filter(Boolean).join('/');
  return [streetPart, neighborhood, cityPart].filter(Boolean).join(' · ') || undefined;
}

/**
 * The agency's own code for a property, when it has one.
 *
 * `external_ids.agency_code` (typed in the form or imported) wins, even when
 * empty: an empty one is an explicit clear and must not fall back to the code
 * a spreadsheet import stored. That spreadsheet code (`external_ids.spreadsheet`,
 * e.g. "AP-0101") is the fallback. The code is a label the agency writes on
 * its keys, not an identifier: it may repeat across properties. Callers keep
 * their own fallback for a property with no code (the internal id) -- this
 * only ever overrides it. Mirrors `property_agency_code` in the backend.
 *
 * @param property - Property payload.
 * @returns The trimmed code, or undefined when the property has none.
 */
export function propertyAgencyCode(property: AnyRecord): string | undefined {
  const ids = property.external_ids;
  if (!isPlainRecord(ids)) return undefined;
  return trimmedOrUndefined('agency_code' in ids ? ids.agency_code : ids.spreadsheet);
}

/**
 * Whether a value is a plain object (not null, not an array).
 *
 * @param value - Any value.
 * @returns True for a plain object.
 */
function isPlainRecord(value: unknown): value is AnyRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * The trimmed text of a stored value, or undefined when it is empty.
 *
 * @param value - A stored value (string, number, null or undefined).
 * @returns The trimmed text, or undefined.
 */
function trimmedOrUndefined(value: unknown): string | undefined {
  return String(value ?? '').trim() || undefined;
}
